import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { ChevronLeft, Calendar, ArrowRight, Mail } from "lucide-react";
import { fetchBrief } from "@/lib/api";
import NewsletterForm from "@/components/NewsletterForm";
import useSEO from "@/lib/useSEO";

const formatDate = (iso) => {
  try { return new Date(iso).toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" }); } catch { return ""; }
};

export default function BriefDetail() {
  const { id } = useParams();
  const [brief, setBrief] = useState(null);
  const [notFound, setNotFound] = useState(false);

  useSEO({
    title: brief ? `${brief.subject} | The Brief · Upload Counsel` : "The Brief · Upload Counsel",
    description: brief?.intro,
    canonical: brief ? `https://uploadlawyer.com/brief/${brief.id}` : undefined,
  });

  useEffect(() => {
    (async () => {
      setBrief(null); setNotFound(false);
      try {
        const data = await fetchBrief(id);
        setBrief(data.brief);
      } catch { setNotFound(true); }
    })();
  }, [id]);

  if (notFound) return (
    <div className="max-w-3xl mx-auto px-6 py-32 text-center">
      <h1 className="font-display text-4xl font-bold text-white">Edition not found</h1>
      <Link to="/brief" className="mt-6 inline-flex items-center gap-1 text-[#D9B36C]"><ChevronLeft size={14}/> Back to archive</Link>
    </div>
  );
  if (!brief) return <div className="max-w-3xl mx-auto px-6 py-32 text-slate-500">Loading…</div>;

  return (
    <div data-testid={`page-brief-${id}`}>
      <article className="relative">
        <div className="max-w-3xl mx-auto px-6 lg:px-8 pt-24 pb-16 md:pt-32">
          <Link to="/brief" className="inline-flex items-center gap-1 text-sm text-slate-400 hover:text-[#D9B36C]">
            <ChevronLeft size={14} /> Back to archive
          </Link>

          <div className="mt-8 inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-[#D9B36C]/40 bg-[#D9B36C]/10 text-[#D9B36C] text-xs font-semibold uppercase tracking-widest">
            <Mail size={12} /> The Brief · {formatDate(brief.sent_at)}
          </div>

          <h1 className="mt-6 font-display text-4xl md:text-5xl font-extrabold text-white tracking-tight leading-tight">{brief.subject}</h1>
          <p className="mt-6 text-slate-300 text-lg leading-relaxed">{brief.intro}</p>

          <div className="mt-12 space-y-10">
            {(brief.items || []).map((it, i) => (
              <div key={i} data-testid={`brief-item-${i}`} className="border-t border-[#272B36] pt-8">
                <a href={it.url} target="_blank" rel="noreferrer" className="font-display text-2xl font-semibold text-white hover:text-[#D9B36C] transition-colors">{it.headline}</a>
                <p className="mt-2 text-xs text-slate-500 uppercase tracking-widest">{it.source}</p>
                <p className="mt-4 text-slate-300 leading-relaxed">
                  <span className="text-[#D9B36C] font-semibold">Why it matters: </span>{it.why_it_matters}
                </p>
              </div>
            ))}
          </div>

          <div className="mt-12 border-t border-[#272B36] pt-8">
            <p className="text-slate-300 leading-relaxed">{brief.closer}</p>
          </div>

          <div className="mt-12 rounded-2xl border border-[#272B36] bg-gradient-to-br from-[#14161D] to-[#121419] p-8">
            <p className="text-xs uppercase tracking-[0.25em] font-semibold text-[#D9B36C]">Have a matter?</p>
            <p className="mt-2 font-display font-semibold text-white text-xl">Have a problem like this? Send a request.</p>
            <p className="mt-2 text-sm text-slate-400 leading-relaxed">Bring us your platform dispute, brand deal, trademark question, or entity situation. Scope + price visible before any engagement.</p>
            <Link to="/start" className="mt-4 inline-flex items-center gap-2 bg-[#D9B36C] text-black font-semibold px-6 py-3 rounded-full text-sm">Start a request <ArrowRight size={14}/></Link>
          </div>

          <div className="mt-8 rounded-2xl border border-[#272B36] bg-[#121419] p-6">
            <p className="text-xs uppercase tracking-[0.25em] font-semibold text-[#D9B36C]">Get The Brief weekly</p>
            <p className="mt-2 text-sm text-slate-400">Every Friday. One short read. Unsubscribe in one click.</p>
            <div className="mt-4"><NewsletterForm source={`brief-${id}`} /></div>
          </div>
        </div>
      </article>
    </div>
  );
}
